import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import axiosInstance from "../utils/axiosInstance";
import { missingRequired, toastApiError, toastErrors } from "../utils/toastErrors";

const EMPTY_FORMS = {
  seller: {
    seller_name: "",
    seller_address: "",
    seller_country: "",
    seller_bank_name: "",
    seller_account_name: "",
    seller_iban: "",
    seller_swift: "",
  },
  buyer: {
    buyer_name: "",
    buyer_card_number: "",
    buyer_address: "",
    buyer_tel: "",
  },
};

const inputClassName =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-100";

const LABELS = {
  seller: {
    seller_name: "نام فروشنده",
    seller_country: "کشور",
    seller_address: "آدرس",
    seller_bank_name: "نام بانک",
    seller_account_name: "نام صاحب حساب",
    seller_iban: "شماره شبا (IBAN)",
    seller_swift: "کد SWIFT",
  },
  buyer: {
    buyer_name: "نام خریدار",
    buyer_card_number: "شماره کارت",
    buyer_address: "آدرس",
    buyer_tel: "شماره تماس",
  },
};

const REQUIRED = {
  seller: ["seller_name", "seller_address"],
  buyer: ["buyer_name", "buyer_card_number", "buyer_address"],
};

export default function InvoicePartyQuickCreate({ type, onClose, onCreated }) {
  const [values, setValues] = useState(() => ({ ...EMPTY_FORMS[type] }));
  const [saving, setSaving] = useState(false);

  const isSeller = type === "seller";
  const title = isSeller ? "ایجاد فروشنده جدید" : "ایجاد خریدار جدید";

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, saving]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const labels = LABELS[type];
    const problems = missingRequired(
      values,
      Object.fromEntries(REQUIRED[type].map((name) => [name, labels[name]])),
    );
    if (problems.length) {
      toastErrors(problems);
      return;
    }
    setSaving(true);
    try {
      const endpoint = isSeller ? "documents/sellers/" : "documents/buyers/";
      const response = await axiosInstance.post(endpoint, values);
      onCreated(response.data);
      toast.success(isSeller ? "فروشنده ایجاد و انتخاب شد." : "خریدار ایجاد و انتخاب شد.");
      onClose();
    } catch (error) {
      toastApiError(error, LABELS[type], "ذخیره اطلاعات با خطا مواجه شد.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4"
      dir="rtl"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        noValidate
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
            title="بستن"
            aria-label="بستن"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
          {isSeller ? (
            <>
              <Field label="نام فروشنده" name="seller_name" value={values.seller_name} onChange={handleChange} required autoFocus />
              <Field label="کشور" name="seller_country" value={values.seller_country} onChange={handleChange} />
              <Field label="آدرس" name="seller_address" value={values.seller_address} onChange={handleChange} required className="md:col-span-2" />
              <Field label="نام بانک" name="seller_bank_name" value={values.seller_bank_name} onChange={handleChange} />
              <Field label="نام صاحب حساب" name="seller_account_name" value={values.seller_account_name} onChange={handleChange} />
              <Field label="شماره شبا (IBAN)" name="seller_iban" value={values.seller_iban} onChange={handleChange} dir="ltr" />
              <Field label="کد SWIFT" name="seller_swift" value={values.seller_swift} onChange={handleChange} dir="ltr" />
            </>
          ) : (
            <>
              <Field label="نام خریدار" name="buyer_name" value={values.buyer_name} onChange={handleChange} required autoFocus />
              <Field label="شماره کارت" name="buyer_card_number" value={values.buyer_card_number} onChange={handleChange} required dir="ltr" />
              <Field label="آدرس" name="buyer_address" value={values.buyer_address} onChange={handleChange} required className="md:col-span-2" />
              <Field label="شماره تماس" name="buyer_tel" value={values.buyer_tel} onChange={handleChange} dir="ltr" />
            </>
          )}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            انصراف
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex min-w-28 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "در حال ذخیره" : "ایجاد و انتخاب"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

function Field({ label, className = "", dir = "rtl", ...inputProps }) {
  return (
    <label className={`space-y-1 ${className}`}>
      <span className="text-sm text-gray-700">
        {label}
        {inputProps.required ? <span className="mr-1 text-red-600">*</span> : null}
      </span>
      <input {...inputProps} dir={dir} className={inputClassName} />
    </label>
  );
}
